import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  GraduationCap, BookOpen, Users, Plus, Loader2, Upload,
  ArrowRight, CheckCircle2, Briefcase, FileText, Search
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EducatorDashboardPage = () => {
  const [tab, setTab] = useState('browse'); // browse | my-courses | upgrade
  const [educators, setEducators] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEducator, setIsEducator] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showPublish, setShowPublish] = useState(null); // course to publish to
  const [lectures, setLectures] = useState([]);
  const [selectedLecture, setSelectedLecture] = useState('');

  // Upgrade form
  const [upgradeForm, setUpgradeForm] = useState({ title: 'Mr./Mrs.', department: '', institution: '', courses: '', bio: '' });
  const [upgrading, setUpgrading] = useState(false);

  // Create course form
  const [courseForm, setCourseForm] = useState({ name: '', code: '', description: '', semester: '' });
  const [creatingCourse, setCreatingCourse] = useState(false);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [eduRes, courseRes] = await Promise.all([
          axios.get(`${API}/educators/browse`),
          axios.get(`${API}/educators/courses`).catch(() => ({ data: { courses: [] } })),
        ]);
        setEducators(eduRes.data.educators || []);
        const courses = courseRes.data.courses || [];
        setMyCourses(courses);
        setIsEducator(courses.length > 0);
        if (courses.length > 0) setTab('my-courses');

        axios.get(`${API}/lectures`).then(r => setLectures(r.data || [])).catch(() => {});
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    init();
  }, []);

  const handleUpgrade = async () => {
    if (!upgradeForm.department.trim() || !upgradeForm.institution.trim()) return toast.error('Fill in department and institution');
    setUpgrading(true);
    try {
      const res = await axios.post(`${API}/educators/upgrade`, {
        ...upgradeForm,
        courses: upgradeForm.courses.split(',').map(c => c.trim()).filter(Boolean),
      });
      toast.success(res.data.status === 'already_educator' ? 'Already an educator!' : `Upgraded! ${res.data.courses_created} courses created.`);
      setIsEducator(true);
      setTab('my-courses');
      const courseRes = await axios.get(`${API}/educators/courses`);
      setMyCourses(courseRes.data.courses || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setUpgrading(false);
  };

  const handleCreateCourse = async () => {
    if (!courseForm.name.trim()) return toast.error('Enter course name');
    setCreatingCourse(true);
    try {
      await axios.post(`${API}/educators/courses`, courseForm);
      toast.success('Course created!');
      setShowCreate(false);
      setCourseForm({ name: '', code: '', description: '', semester: '' });
      const res = await axios.get(`${API}/educators/courses`);
      setMyCourses(res.data.courses || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setCreatingCourse(false);
  };

  const handlePublish = async (courseId) => {
    if (!selectedLecture) return toast.error('Select a lecture');
    try {
      await axios.post(`${API}/educators/courses/${courseId}/publish`, {
        course_id: courseId,
        lecture_id: selectedLecture,
        visibility: 'enrolled',
      });
      toast.success('Material published!');
      setShowPublish(null);
      const res = await axios.get(`${API}/educators/courses`);
      setMyCourses(res.data.courses || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleEnroll = async (courseId) => {
    try {
      const res = await axios.post(`${API}/educators/courses/${courseId}/enroll`);
      toast.success(res.data.status === 'already_enrolled' ? 'Already enrolled!' : `Enrolled in ${res.data.course_name}!`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="educator-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl sm:text-4xl font-bold text-[var(--isp-heading)] flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-white" />
          </div>
          Educators
        </h1>
        {isEducator && (
          <Button onClick={() => setShowCreate(true)} className="rounded-xl gap-2 bg-gradient-to-r from-violet-500 to-purple-500" data-testid="create-course-btn">
            <Plus className="w-4 h-4" /> New Course
          </Button>
        )}
      </div>

      <div className="flex gap-2">
        {[
          { id: 'browse', label: 'Browse Educators' },
          ...(isEducator ? [{ id: 'my-courses', label: 'My Courses' }] : []),
          ...(!isEducator ? [{ id: 'upgrade', label: 'Become an Educator' }] : []),
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${tab === t.id ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30' : 'text-[var(--isp-body)] hover:bg-[var(--isp-glass-inner)]'}`}
            data-testid={`edu-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'browse' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="educators-grid">
          {educators.length === 0 ? (
            <Card className="col-span-2 p-10 text-center  bg-[var(--isp-glass)] border-[var(--isp-glass-border)]">
              <GraduationCap className="w-10 h-10 mx-auto mb-3 text-[var(--isp-muted)]" />
              <p className="text-[var(--isp-body)]">No educators yet. Upgrade your account to be the first!</p>
            </Card>
          ) : educators.map((e, i) => (
            <motion.div key={e.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
              <Card className=" bg-[var(--isp-glass)] border-[var(--isp-glass-border)] p-4" data-testid={`educator-${e.id}`}>
                <div className="flex items-start gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    {(e.name || 'E')[0]}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[var(--isp-heading)]">{e.name}</p>
                    <p className="text-[10px] text-[var(--isp-muted)]">{e.department} · {e.institution}</p>
                  </div>
                </div>
                {e.bio && <p className="text-xs text-[var(--isp-muted)] mb-2 line-clamp-2">{e.bio}</p>}
                <div className="flex gap-3 text-[10px] text-[var(--isp-muted)]">
                  <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {e.materials_published} materials</span>
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {(e.courses || []).length} courses</span>
                </div>
                {(e.courses || []).length > 0 && (
                  <div className="mt-3 space-y-1">
                    {e.courses.slice(0, 3).map((c, ci) => (
                      <div key={ci} className="flex items-center justify-between px-2 py-1.5 rounded-lg bg-white/[0.03]">
                        <span className="text-xs text-[var(--isp-body)]">{c.name}</span>
                        <Button size="sm" onClick={() => handleEnroll(c.id)} className="h-6 px-2 rounded text-[10px] bg-violet-500/20 text-violet-300" data-testid={`enroll-${c.id}`}>
                          Enroll
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {tab === 'my-courses' && (
        <div className="space-y-4" data-testid="my-courses">
          {myCourses.length === 0 ? (
            <Card className="p-10 text-center  bg-[var(--isp-glass)] border-[var(--isp-glass-border)]">
              <BookOpen className="w-10 h-10 mx-auto mb-3 text-[var(--isp-muted)]" />
              <p className="text-[var(--isp-body)]">No courses yet. Create your first!</p>
            </Card>
          ) : myCourses.map(c => (
            <Card key={c.id} className=" bg-[var(--isp-glass)] border-[var(--isp-glass-border)] p-5" data-testid={`course-${c.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-base font-semibold text-[var(--isp-heading)]">{c.name}</h3>
                  <p className="text-xs text-[var(--isp-muted)]">{c.code && `${c.code} · `}{c.student_count} students · {c.material_count} materials{c.semester && ` · ${c.semester}`}</p>
                </div>
                <Button size="sm" onClick={() => { setShowPublish(c); setSelectedLecture(''); }}
                  className="rounded-lg gap-1 text-xs bg-gradient-to-r from-violet-500 to-purple-500" data-testid={`publish-to-${c.id}`}>
                  <Upload className="w-3 h-3" /> Publish Material
                </Button>
              </div>
              {c.description && <p className="text-xs text-[var(--isp-muted)]">{c.description}</p>}
            </Card>
          ))}
        </div>
      )}

      {tab === 'upgrade' && (
        <Card className="max-w-md mx-auto p-6  bg-[var(--isp-glass)] border-[var(--isp-glass-border)] space-y-4" data-testid="educator-upgrade">
          <h2 className="text-lg font-semibold text-[var(--isp-heading)] flex items-center gap-2"><GraduationCap className="w-5 h-5 text-violet-400" /> Become an Educator</h2>
          <p className="text-xs text-[var(--isp-muted)]">Upgrade your account to publish course materials for your students. You keep all student features.</p>
          <select value={upgradeForm.title} onChange={e => setUpgradeForm(p => ({ ...p, title: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-[var(--isp-glass)] border border-[var(--isp-glass-border)] text-sm text-[var(--isp-body)]" data-testid="up-title">
            {['Mr./Mrs.', 'Dr.', 'Prof.', 'Assoc. Prof.', 'Lecturer'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <Input value={upgradeForm.department} onChange={e => setUpgradeForm(p => ({ ...p, department: e.target.value }))} placeholder="Department" className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="up-dept" />
          <Input value={upgradeForm.institution} onChange={e => setUpgradeForm(p => ({ ...p, institution: e.target.value }))} placeholder="Institution" className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="up-inst" />
          <Input value={upgradeForm.courses} onChange={e => setUpgradeForm(p => ({ ...p, courses: e.target.value }))} placeholder="Courses (comma separated)" className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="up-courses" />
          <Textarea value={upgradeForm.bio} onChange={e => setUpgradeForm(p => ({ ...p, bio: e.target.value }))} placeholder="Bio (optional)" className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="up-bio" />
          <Button onClick={handleUpgrade} disabled={upgrading} className="w-full rounded-xl bg-gradient-to-r from-violet-500 to-purple-500" data-testid="up-submit">
            {upgrading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upgrade to Educator'}
          </Button>
        </Card>
      )}

      {/* Create Course Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[var(--isp-bg)] border-[var(--isp-glass-border)] text-[var(--isp-heading)] max-w-sm" data-testid="create-course-dialog">
          <DialogHeader><DialogTitle>Create Course</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={courseForm.name} onChange={e => setCourseForm(p => ({ ...p, name: e.target.value }))} placeholder="Course Name" className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="cc-name" />
            <Input value={courseForm.code} onChange={e => setCourseForm(p => ({ ...p, code: e.target.value }))} placeholder="Course Code (e.g. BIO101)" className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="cc-code" />
            <Textarea value={courseForm.description} onChange={e => setCourseForm(p => ({ ...p, description: e.target.value }))} placeholder="Description" className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="cc-desc" />
            <Input value={courseForm.semester} onChange={e => setCourseForm(p => ({ ...p, semester: e.target.value }))} placeholder="Semester (e.g. Fall 2026)" className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="cc-sem" />
            <Button onClick={handleCreateCourse} disabled={creatingCourse} className="w-full rounded-xl bg-gradient-to-r from-violet-500 to-purple-500" data-testid="cc-submit">
              {creatingCourse ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Course'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Publish Material Dialog */}
      <Dialog open={!!showPublish} onOpenChange={() => setShowPublish(null)}>
        <DialogContent className="bg-[var(--isp-bg)] border-[var(--isp-glass-border)] text-[var(--isp-heading)] max-w-sm" data-testid="publish-dialog">
          <DialogHeader><DialogTitle>Publish to {showPublish?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <select value={selectedLecture} onChange={e => setSelectedLecture(e.target.value)} className="w-full px-3 py-2 rounded-xl bg-[var(--isp-glass)] border border-[var(--isp-glass-border)] text-sm" data-testid="pub-lecture">
              <option value="">Select a lecture</option>
              {lectures.map(l => <option key={l.id} value={l.id}>{l.title}</option>)}
            </select>
            <Button onClick={() => handlePublish(showPublish?.id)} className="w-full rounded-xl bg-gradient-to-r from-violet-500 to-purple-500" data-testid="pub-submit">
              Publish Material
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EducatorDashboardPage;
